import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { AlertCircle as AlertCircleIcon } from 'lucide-react-native';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Spinner } from '@/components/ui/spinner';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useGoBack } from '@/hooks/useGoBack';
import { useToaster } from '@/hooks/useToaster';
import { getUserV2QueryOptions } from '@/services/getUserV2';
import { userIdAtom } from '@/services/globalState';
import { updateUser } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';

const aboutSchema = z.object({
  about: z.string().optional(),
});

function EditAboutScreen() {
  const userId = useAtomValue(userIdAtom);

  const goBack = useGoBack(ROUTES.ACCOUNT_PROFILE);

  const queryClient = useQueryClient();

  const toaster = useToaster();

  const { bottom } = useSafeAreaInsets();

  const { data: userData, isLoading } = useQuery(getUserV2QueryOptions(userId));

  const { mutateAsync: updateUserMutation } = useMutation({ mutationFn: updateUser });

  const aboutForm = useForm({
    defaultValues: { about: userData?.blurb || '' },
    resolver: zodResolver(aboutSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (userData) {
      aboutForm.reset({ about: userData?.blurb || '' });
    }
  }, [userData]);

  const handleBack = () => {
    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({
    isDirty: aboutForm.formState.isDirty,
    onDiscard: handleBack,
  });

  const onSubmit = aboutForm.handleSubmit(async (values) => {
    try {
      await updateUserMutation({ blurb: values.about });

      aboutForm.reset(aboutForm.getValues(), { keepValues: true, keepDirty: false });

      queryClient.invalidateQueries();

      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
        section: 'about',
        previous: userData,
        current: aboutForm.getValues(),
      });

      toaster.showSuccess({ title: 'About me updated' });

      goBack();
    } catch (error) {
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_FAILED, {
        section: 'about',
        previous: userData,
        current: aboutForm.getValues(),
      });

      toaster.showError({
        title: 'Failed to save about me',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  });

  useEffect(() => {
    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
      section: 'about',
    });
  }, []);

  if (isLoading) {
    return <Spinner size="large" className="flex-1" />;
  }

  return (
    <VStack
      className={
        isWeb ? 'mx-auto w-full max-w-screen-md flex-1 px-5 md:px-10' : 'flex-1 bg-background-0'
      }>
      <Header
        contentScrollsUnderHeader
        {...getCancelHeaderProps(confirmCancel)}
        title="Edit About Me"
        rightItems={[
          headerItem.save({
            onPress: onSubmit,
            isDisabled: !aboutForm.formState.isDirty,
            isSaving: aboutForm.formState.isSubmitting,
          }),
        ]}
      />

      {discardModal}

      {!isWeb ? (
        <KeyboardAwareScrollView
          contentInsetAdjustmentBehavior="automatic"
          mode="layout"
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          enabled
          extraKeyboardSpace={120}>
          <VStack space="md" className="px-6 pt-6">
            <FormControl isInvalid={!!aboutForm.formState.errors.about} className="flex-1 flex-col">
              <Textarea
                size="sm"
                className="h-[40vh] max-h-[75vh] resize-y overflow-auto rounded-lg border border-outline-500 p-2 text-base">
                <Controller
                  name="about"
                  control={aboutForm.control}
                  render={({ field }) => (
                    <TextareaInput
                      autoFocus
                      placeholder="Clinical focus, research interests, background"
                      value={field.value}
                      onChangeText={field.onChange}
                      multiline
                      scrollEnabled
                      textAlignVertical="top"
                      className="flex-1 text-base"
                    />
                  )}
                />
              </Textarea>
              <FormControlError className="mt-1">
                <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                <FormControlErrorText size="xs">
                  {aboutForm.formState.errors.about?.message as string}
                </FormControlErrorText>
              </FormControlError>
            </FormControl>
          </VStack>
        </KeyboardAwareScrollView>
      ) : (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag">
          <VStack space="md" className="px-6 pt-6">
            <FormControl isInvalid={!!aboutForm.formState.errors.about} className="flex-1 flex-col">
              <Textarea
                size="sm"
                className="h-[30vh] max-h-[75vh] resize-y overflow-auto rounded-lg border border-outline-500 p-2 text-base md:min-h-[300px] lg:min-h-[400px]">
                <Controller
                  name="about"
                  control={aboutForm.control}
                  render={({ field }) => (
                    <TextareaInput
                      autoFocus
                      placeholder="Clinical focus, research interests, background"
                      value={field.value}
                      onChangeText={field.onChange}
                      multiline
                      scrollEnabled
                      textAlignVertical="top"
                      className="flex-1 text-base"
                    />
                  )}
                />
              </Textarea>
              <FormControlError className="mt-1">
                <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                <FormControlErrorText size="xs">
                  {aboutForm.formState.errors.about?.message as string}
                </FormControlErrorText>
              </FormControlError>
            </FormControl>
          </VStack>
        </ScrollView>
      )}
    </VStack>
  );
}

export default EditAboutScreen;
